import React from "react";

import { IconCheckCircle } from "@/components/design-system/basic-icons/IconCheckCircle";
import { IconX } from "@/components/design-system/basic-icons/IconX";
import { SectionHeader } from "@/components/design-system/SectionHeader/SectionHeader";
import { cn } from "@/utils/cn";

type SolutionSectionProps = {
  readonly label: string;
  readonly heading: React.ReactNode;
  readonly description: string;
  readonly leftTitle?: string;
  readonly rightTitle?: string;
  readonly leftItems: readonly string[];
  readonly rightItems: readonly string[];
  readonly className?: string;
  readonly labelClassName?: string;
  readonly headingClassName?: string;
  readonly backgroundSrc?: string;
};

export const SolutionSection: React.FC<SolutionSectionProps> = ({
  label,
  heading,
  description,
  leftTitle = "Without Bravo",
  rightTitle = "With Bravo",
  leftItems,
  rightItems,
  className,
  labelClassName,
  headingClassName,
  backgroundSrc,
}) => {
  return (
    <section
      className={cn("relative w-full overflow-hidden bg-white", className)}
    >
      {backgroundSrc && (
        <img
          src={backgroundSrc}
          alt=""
          aria-hidden
          className="absolute inset-0 hidden size-full object-cover opacity-20 md:block"
        />
      )}
      <div className="section-inset relative py-12 md:py-24">
        <SectionHeader
          label={label}
          heading={heading}
          description={description}
          className="relative z-10 mb-12 md:mb-16"
          labelClassName={labelClassName}
          headingClassName={headingClassName}
        />

        <div className="relative z-10 flex flex-col items-start gap-14 md:flex-row md:items-center md:justify-center md:gap-12 xl:mx-auto xl:max-w-282">
          <div className="md:flex-1 xl:max-w-115">
            <p className="subtitle-b-semibold mb-6 text-black">{leftTitle}</p>
            <div className="flex flex-col gap-6">
              {leftItems.map((text) => (
                <div key={text} className="flex items-center gap-2">
                  <IconX className="h-6 w-6 shrink-0" />
                  <span className="body-b-regular text-black">{text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative md:min-w-0 md:flex-1">
            <div
              className="relative z-10 rounded-2xl bg-white p-8"
              style={{
                boxShadow:
                  "6px 6px 30px 0px rgba(182,98,166,0.7), -6px -6px 30px 0px rgba(242,185,75,0.7)",
              }}
            >
              <p className="subtitle-b-semibold mb-6 text-black">
                {rightTitle}
              </p>
              <div className="flex flex-col gap-6">
                {rightItems.map((text) => (
                  <div key={text} className="flex items-center gap-2">
                    <IconCheckCircle
                      className="h-6 w-6 shrink-0"
                      fillColor="var(--color-accent-blue-bg)"
                      strokeColor="var(--color-accent-blue)"
                    />
                    <span className="body-b-regular text-black">{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
